import { FC, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../assets/hooks/hooks";
import { initializedActions } from "../../redux/initial-app-slice";
import { AuthData } from "../../redux/auth-action";
import { PermissionRoles } from "../../models/models";
import { getPermissionsFromToken } from "../../assets/helpers/authToken";

// .NET ClaimTypes emitted by the backend JWT (see token payload).
const CLAIM_ID =
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
const CLAIM_NAME = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";
const CLAIM_ROLE =
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

type BackendJwtClaims = {
    [CLAIM_ID]?: string;
    [CLAIM_NAME]?: string;
    [CLAIM_ROLE]?: string;
};

// Backend redirects to <origin>/auth/callback#token=<jwt> after Microsoft login.
const parseTokenFromHash = (): string | null => {
    const hash = window.location.hash.replace(/^#/, "");
    return new URLSearchParams(hash).get("token");
};

const buildAuthData = (token: string): AuthData => {
    const claims = jwtDecode<BackendJwtClaims>(token);
    return {
        id: Number(claims[CLAIM_ID] ?? -1),
        userName: claims[CLAIM_NAME] ?? "",
        role: (claims[CLAIM_ROLE] ?? "user") as PermissionRoles,
        permissions: getPermissionsFromToken(token),
        token,
        isAuth: true,
    };
};

const AuthCallback: FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const handled = useRef(false);

    useEffect(() => {
        // Guard against double-run in React 18 StrictMode.
        if (handled.current) return;
        handled.current = true;

        const token = parseTokenFromHash();
        if (!token) {
            toast.error("Microsoft login failed: no token received");
            navigate("/auth", { replace: true });
            return;
        }

        try {
            const authData = buildAuthData(token);
            localStorage.setItem("auth", JSON.stringify(authData));
            dispatch(
                initializedActions.initializedSuccess({
                    userId: authData.id,
                    token: authData.token,
                    userName: authData.userName,
                    role: authData.role,
                    permissions: authData.permissions,
                    initialized: true,
                    theme: "dark",
                }),
            );
            // Strip the token from the address bar before entering the app.
            window.history.replaceState(null, "", window.location.pathname);
            navigate("/", { replace: true });
        } catch {
            toast.error("Microsoft login failed: invalid token");
            navigate("/auth", { replace: true });
        }
    }, [dispatch, navigate]);

    return null;
};

export default AuthCallback;
